import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cleave from "cleave.js/react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { ADMIN_ROLES } from "../../../../Routes/roleAccess";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  FormText,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
  Badge,
  UncontrolledDropdown,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import ProfessionalAvatar from "../../../../Components/Common/ProfessionalAvatar";
import { resolvePublicStorageUrl } from "../../../../helpers/professionalAvatar";
import { HELP_ARTICLE_INVITE_TEAM, HELP_ARTICLE_PROFESSIONALS } from "../../../../helpers/helpCenter";
import { entityFormKey } from "../../../../helpers/entityEditForm";
import {
  formatInviteCooldownSeconds,
  getInviteResendCooldownRemainingMs,
} from "../../../../helpers/inviteCooldown";
import {
  handleInviteShareChoice,
  promptInviteShare,
} from "../../../../helpers/inviteShare";
import {
  formatCentsToMoneyInput,
  parseMoneyInputToCents,
} from "../../../../helpers/money";
import {
  createProfessional,
  deactivateProfessional,
  deleteProfessional,
  deleteProfessionalPhoto,
  getProfessional,
  getProfessionalGoals,
  getProfessionals,
  inviteProfessional,
  Professional,
  ProfessionalServiceCommissionOverride,
  restoreProfessional,
  updateProfessional,
  uploadProfessionalPhoto,
} from "../../../../services/professionalService";
import {
  DEFAULT_TENANT_SETTINGS,
  getSettings,
  TenantSettings,
} from "../../../../services/tenantSettingService";
import {
  getProfessionalServices,
  ProfessionalServicesData,
  syncProfessionalServices,
} from "../../../../services/professionalServiceEnablementService";
import { PaginationMeta } from "../../../../types/api";
import { userHasAnyRole } from "../../../../services/authService";
import ServiceCoverageNotice from "../../../../Components/Studio/ServiceCoverageNotice";
import ProfessionalRosterLines from "./ProfessionalRosterLines";
import {
  markProfessionalRosterUpdated,
  rosterUpdateNotice,
  subscribeProfessionalRosterUpdates,
} from "./professionalRosterRefresh";
import ProfessionalPhotoField from "./ProfessionalPhotoField";
import ProfessionalScheduleModal from "./ProfessionalScheduleModal";
import ProfessionalServiceCommissionPanel from "./ProfessionalServiceCommissionPanel";
import ScheduleBlocksModal from "./ScheduleBlocksModal";
import ProfessionalCommissionSummaryPanel from "./ProfessionalCommissionSummaryPanel";
import ProfessionalGoalsProgressPanel from "./ProfessionalGoalsProgressPanel";
import { getStatus } from "../../../../services/onboardingService";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;
const FUTURE_APPOINTMENTS_ERROR_CODE = "PROFESSIONAL_HAS_FUTURE_APPOINTMENTS";

type ProfessionalApiError = {
  data?: string;
  code?: string;
  details?: Record<string, string[]>;
};

const extractPhoneDigits = (value: string): string => value.replace(/\D/g, "");

const extractFirstValidationMessage = (error: ProfessionalApiError): string | null => {
  const details = error.details;

  if (!details) {
    return null;
  }

  for (const messages of Object.values(details)) {
    if (Array.isArray(messages) && messages.length > 0 && messages[0]) {
      return messages[0];
    }
  }

  return null;
};

const applyProfessionalValidationErrors = (
  error: ProfessionalApiError,
  setFieldError: (field: string, message: string) => void,
  setFieldTouched: (field: string, touched: boolean, shouldValidate?: boolean) => void,
  t: (key: string) => string
): boolean => {
  if (error.code !== "VALIDATION_ERROR" || !error.details) {
    return false;
  }

  const fieldMap: Record<string, keyof ProfessionalFormValues> = {
    name: "name",
    email: "email",
    phone_number: "phone_display",
    country_code: "phone_display",
    commission_rate_bps: "commission_percent",
    weekly_commission_goal_cents: "weekly_commission_goal_display",
    weekly_appointments_goal: "weekly_appointments_goal",
    monthly_commission_goal_cents: "monthly_commission_goal_display",
    monthly_appointments_goal: "monthly_appointments_goal",
    color_hex: "color_hex",
  };

  let applied = false;

  Object.entries(error.details).forEach(([field, messages]) => {
    const formField = fieldMap[field];

    if (!formField || !Array.isArray(messages) || messages.length === 0) {
      return;
    }

    setFieldError(formField, messages[0]);
    setFieldTouched(formField, true, false);
    applied = true;
  });

  if (!applied) {
    const firstDetail = extractFirstValidationMessage(error);

    if (firstDetail) {
      toast.error(firstDetail);
      return true;
    }
  }

  return applied;
};

const resolveProfessionalSaveErrorMessage = (
  error: ProfessionalApiError,
  fallback: string
): string => {
  if (error.code === "VALIDATION_ERROR") {
    const firstDetail = extractFirstValidationMessage(error);

    if (firstDetail) {
      return firstDetail;
    }
  }

  return error.data || fallback;
};

const isProfessionalActive = (professional: Professional): boolean =>
  professional.is_active !== false;

const resolveProfessionalAccessStatus = (
  professional: Professional
): "none" | "pending" | "active" => {
  if (professional.access_status) {
    return professional.access_status;
  }

  return professional.user_id != null ? "active" : "none";
};

const formatInviteSentAt = (
  invitedAt: string | null | undefined,
  locale: string
): string | null => {
  if (!invitedAt) {
    return null;
  }

  const parsed = new Date(invitedAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
};

type ProfessionalFormTab = "data" | "enablement" | "commissions";

interface ProfessionalFormValues {
  name: string;
  commission_percent: string;
  weekly_commission_goal_display: string;
  weekly_appointments_goal: string;
  monthly_commission_goal_display: string;
  monthly_appointments_goal: string;
  color_hex: string;
  email: string;
  phone_display: string;
}

const formatPhoneDisplay = (
  countryCode: string | null | undefined,
  phoneNumber: string | null | undefined
): string => {
  if (!phoneNumber) {
    return "";
  }

  const digits = phoneNumber.replace(/\D/g, "");
  if (digits.length <= 2) {
    return digits.length ? `(${digits}` : "";
  }
  if (digits.length <= 7) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

const bpsToPercent = (bps: number): string => {
  const value = bps / 100;
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
};

const percentToBps = (percent: string | number): number => {
  const normalized = String(percent).replace(",", ".").trim();
  return Math.round(parseFloat(normalized) * 100);
};

const formatServiceCommissionPreview = (
  professional: Professional,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null => {
  const overrides = professional.service_commission_overrides ?? [];
  if (overrides.length === 0) {
    return null;
  }

  const defaultLabel = bpsToPercent(professional.commission_rate_bps);
  const parts = overrides.map(
    (item) => `${item.service_name} ${bpsToPercent(item.commission_rate_bps)}%`
  );
  const total = professional.service_commission_overrides_total ?? overrides.length;
  const more = total > overrides.length ? total - overrides.length : 0;
  const specials = more > 0
    ? `${parts.join(" · ")} · ${t("studio.professionals.serviceCommissions.listMore", { count: more })}`
    : parts.join(" · ");

  return t("studio.professionals.serviceCommissions.listPreview", {
    specials,
    percent: defaultLabel,
  });
};

const normalizeHexColor = (value: string): string => {
  const hex = value.replace(/^#/, "");
  return `#${hex.toUpperCase()}`;
};

const emptyFormValues = (): ProfessionalFormValues => ({
  name: "",
  commission_percent: "",
  weekly_commission_goal_display: "",
  weekly_appointments_goal: "",
  monthly_commission_goal_display: "",
  monthly_appointments_goal: "",
  color_hex: "#119CF1",
  email: "",
  phone_display: "",
});

const mapProfessionalToFormValues = (
  professional: Professional,
  locale: string
): ProfessionalFormValues => ({
  name: professional.name,
  commission_percent: bpsToPercent(professional.commission_rate_bps),
  weekly_commission_goal_display:
    professional.weekly_commission_goal_cents != null
      ? formatCentsToMoneyInput(professional.weekly_commission_goal_cents, locale)
      : "",
  weekly_appointments_goal:
    professional.weekly_appointments_goal != null
      ? String(professional.weekly_appointments_goal)
      : "",
  monthly_commission_goal_display:
    professional.monthly_commission_goal_cents != null
      ? formatCentsToMoneyInput(professional.monthly_commission_goal_cents, locale)
      : "",
  monthly_appointments_goal:
    professional.monthly_appointments_goal != null
      ? String(professional.monthly_appointments_goal)
      : "",
  color_hex: normalizeHexColor(professional.color_hex),
  email: professional.email ?? "",
  phone_display: formatPhoneDisplay(professional.country_code, professional.phone_number),
});

const ProfessionalsList = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandledRef = React.useRef(false);
  const openEditRef = useRef<
    (professional: Professional, initialTab?: ProfessionalFormTab) => void
  >(() => undefined);
  const openScheduleRef = useRef<(professional: Professional) => void>(() => undefined);

  document.title = t("professionals.pageTitle");

  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [inviteUrlByProfessionalId, setInviteUrlByProfessionalId] = useState<
    Record<number, string>
  >({});
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [onlyServiceCommissions, setOnlyServiceCommissions] = useState(false);
  const serviceCommissionFocusId = Number(searchParams.get("service_commission") || 0) || null;
  const serviceCommissionFocusName = searchParams.get("service_commission_name") || "";
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editingProfessional, setEditingProfessional] = useState<Professional | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [actionTargetId, setActionTargetId] = useState<number | null>(null);
  const [scheduleTarget, setScheduleTarget] = useState<Professional | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [absencesTarget, setAbsencesTarget] = useState<Professional | null>(null);
  const [absencesOpen, setAbsencesOpen] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<Professional | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [pendingPhotoPreview, setPendingPhotoPreview] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [professionalFormTab, setProfessionalFormTab] = useState<ProfessionalFormTab>("data");
  const [enablementLoading, setEnablementLoading] = useState(false);
  const [enablementData, setEnablementData] = useState<ProfessionalServicesData | null>(null);
  const [enablementSearch, setEnablementSearch] = useState("");
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings>(DEFAULT_TENANT_SETTINGS);

  useEffect(() => {
    const hasPendingInvite =
      professionals.some(
        (professional) => resolveProfessionalAccessStatus(professional) === "pending"
      ) ||
      (inviteTarget != null &&
        resolveProfessionalAccessStatus(inviteTarget) === "pending");

    if (!hasPendingInvite) {
      return;
    }

    const timer = window.setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [professionals, inviteTarget]);

  useEffect(() => {
    if (!pendingPhotoFile) {
      setPendingPhotoPreview(null);
      return;
    }

    const url = URL.createObjectURL(pendingPhotoFile);
    setPendingPhotoPreview(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [pendingPhotoFile]);

  const clearPendingPhoto = useCallback(() => {
    setPendingPhotoFile(null);
  }, []);

  const patchLocalProfessionalPhoto = useCallback(
    (professionalId: number, photoPath: string | null) => {
      setProfessionals((current) =>
        current.map((item) =>
          item.id === professionalId ? { ...item, photo_path: photoPath } : item
        )
      );
      setEditingProfessional((current) =>
        current && current.id === professionalId
          ? { ...current, photo_path: photoPath }
          : current
      );
    },
    []
  );

  const handlePhotoPick = async (file: File) => {
    if (!editingProfessional) {
      setPendingPhotoFile(file);
      return;
    }

    setPhotoBusy(true);
    try {
      const response = await uploadProfessionalPhoto(editingProfessional.id, file);
      const path = response.data?.photo_path ?? null;
      patchLocalProfessionalPhoto(editingProfessional.id, path);
      toast.success(response.message || t("professionals.toast.photoUploadSuccess"));
    } catch (error: any) {
      toast.error(error?.data || t("professionals.toast.photoUploadError"));
    } finally {
      setPhotoBusy(false);
    }
  };

  const handlePhotoClear = async () => {
    if (!editingProfessional) {
      clearPendingPhoto();
      return;
    }

    if (!editingProfessional.photo_path) {
      return;
    }

    setPhotoBusy(true);
    try {
      const response = await deleteProfessionalPhoto(editingProfessional.id);
      patchLocalProfessionalPhoto(editingProfessional.id, null);
      toast.success(response.message || t("professionals.toast.photoRemoveSuccess"));
    } catch (error: any) {
      toast.error(error?.data || t("professionals.toast.photoRemoveError"));
    } finally {
      setPhotoBusy(false);
    }
  };

  const useCommissionTracking = tenantSettings.use_commission_tracking;
  const restrictProfessionalServices = tenantSettings.restrict_professional_services;
  const canManageServiceCoverage = userHasAnyRole(ADMIN_ROLES);
  const showOnlineBookingPhotos = tenantSettings.show_professional_photos_in_online_booking !== false;

  const shouldShowPhotoWarningBanner = useMemo(() => {
    if (!showOnlineBookingPhotos || loading) {
      return false;
    }

    const activeProfessionals = professionals.filter(isProfessionalActive);

    if (activeProfessionals.length === 0) {
      return false;
    }

    return !activeProfessionals.some((professional) => Boolean(professional.photo_path?.trim()));
  }, [showOnlineBookingPhotos, loading, professionals]);

  useEffect(() => {
    getSettings()
      .then((response) => setTenantSettings(response.data))
      .catch(() => {
        // Mantém default true — alinhado ao backend.
      });
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().required(t("professionals.validation.nameRequired")),
        commission_percent: useCommissionTracking
          ? Yup.number()
              .typeError(t("professionals.validation.commissionType"))
              .required(t("professionals.validation.commissionRequired"))
              .min(0, t("professionals.validation.commissionMin"))
              .max(100, t("professionals.validation.commissionMax"))
          : Yup.mixed().notRequired(),
        weekly_commission_goal_display: Yup.string()
          .trim()
          .test(
            "weekly-commission-goal",
            t("professionals.validation.weeklyCommissionGoalInvalid"),
            (value) => {
              if (!value) {
                return true;
              }

              const cents = parseMoneyInputToCents(value, i18n.language);

              return Number.isFinite(cents) && cents >= 0;
            }
          ),
        weekly_appointments_goal: Yup.number()
          .transform((value, original) =>
            original === "" || original === null || original === undefined ? null : value
          )
          .nullable()
          .typeError(t("professionals.validation.weeklyAppointmentsGoalInvalid"))
          .integer(t("professionals.validation.weeklyAppointmentsGoalInvalid"))
          .min(0, t("professionals.validation.weeklyAppointmentsGoalMin"))
          .max(500, t("professionals.validation.weeklyAppointmentsGoalMax")),
        monthly_commission_goal_display: Yup.string()
          .trim()
          .test(
            "monthly-commission-goal",
            t("professionals.validation.monthlyCommissionGoalInvalid"),
            (value) => {
              if (!value) {
                return true;
              }

              const cents = parseMoneyInputToCents(value, i18n.language);

              return Number.isFinite(cents) && cents >= 0;
            }
          ),
        monthly_appointments_goal: Yup.number()
          .transform((value, original) =>
            original === "" || original === null || original === undefined ? null : value
          )
          .nullable()
          .typeError(t("professionals.validation.monthlyAppointmentsGoalInvalid"))
          .integer(t("professionals.validation.monthlyAppointmentsGoalInvalid"))
          .min(0, t("professionals.validation.monthlyAppointmentsGoalMin"))
          .max(2000, t("professionals.validation.monthlyAppointmentsGoalMax")),
        color_hex: Yup.string()
          .required(t("professionals.validation.colorRequired"))
          .matches(/^#?[0-9A-Fa-f]{6}$/, t("professionals.validation.colorInvalid")),
        email: Yup.string()
          .trim()
          .transform((value) => (value === "" ? null : value))
          .nullable()
          .email(t("professionals.validation.emailInvalid")),
        phone_display: Yup.string()
          .trim()
          .transform((value) => {
            const digits = extractPhoneDigits(String(value ?? ""));

            return digits.length === 0 ? "" : String(value ?? "").trim();
          })
          .test("phone-digits", t("professionals.validation.phoneInvalid"), (value) => {
            const digits = extractPhoneDigits(String(value ?? ""));

            if (digits.length === 0) {
              return true;
            }

            return digits.length >= 10 && digits.length <= 11;
          }),
      }),
    [t, useCommissionTracking, i18n.language]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, showArchived, onlyServiceCommissions]);

  const patchLocalProfessional = useCallback(
    (professionalId: number, updater: (professional: Professional) => Professional) => {
      setProfessionals((current) =>
        current.map((professional) =>
          professional.id === professionalId ? updater(professional) : professional
        )
      );
    },
    []
  );

  const removeLocalProfessional = useCallback((professionalId: number) => {
    setProfessionals((current) =>
      current.filter((professional) => professional.id !== professionalId)
    );
  }, []);

  const fetchProfessionals = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }

    try {
      const response = await getProfessionals({
        page,
        per_page: PER_PAGE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(showArchived ? { include_inactive: 1 } : {}),
        ...(onlyServiceCommissions && useCommissionTracking
          ? { has_service_commission_overrides: 1 }
          : {}),
      });
      setProfessionals(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      if (!options?.silent) {
        toast.error(error?.data || t("professionals.toast.loadError"));
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [page, debouncedSearch, showArchived, onlyServiceCommissions, useCommissionTracking, t]);

  useEffect(() => {
    fetchProfessionals();
  }, [fetchProfessionals]);

  useEffect(() => {
    return subscribeProfessionalRosterUpdates((source, name, kind, professionalId) => {
      void fetchProfessionals({ silent: true });

      if (source !== "other-tab") {
        return;
      }

      const notice = rosterUpdateNotice(name, kind);
      const message = notice.name ? t(notice.key, { name: notice.name }) : t(notice.key);

      toast.info(
        <span>
          {message}
          {professionalId ? (
            <>
              {" "}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                onClick={() => {
                  const person = professionals.find((item) => item.id === professionalId);
                  const tab = kind === "services" ? "enablement" : "data";

                  if (person) {
                    openEditRef.current(person, tab);
                    window.setTimeout(() => {
                      document
                        .getElementById(`professional-row-${professionalId}`)
                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 80);
                    return;
                  }

                  deepLinkHandledRef.current = false;
                  setSearchParams({ open: String(professionalId), tab });
                }}
              >
                {t("studio.professionals.routine.openProfile")}
              </button>
              {kind === "routine" ? (
                <>
                  {" · "}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 align-baseline"
                    onClick={() => {
                      const person = professionals.find(
                        (item) => item.id === professionalId
                      );

                      if (person) {
                        openScheduleRef.current(person);
                        window.setTimeout(() => {
                          document
                            .getElementById(`professional-row-${professionalId}`)
                            ?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }, 80);
                        return;
                      }

                      deepLinkHandledRef.current = false;
                      setSearchParams({ schedule: String(professionalId) });
                    }}
                  >
                    {t("studio.professionals.routine.openSchedule")}
                  </button>
                </>
              ) : null}
            </>
          ) : null}
        </span>,
        { closeOnClick: false }
      );
    });
  }, [fetchProfessionals, professionals, setSearchParams, t]);

  const formInitialValues = useMemo(
    () =>
      editingProfessional
        ? mapProfessionalToFormValues(editingProfessional, i18n.language)
        : emptyFormValues(),
    [editingProfessional, i18n.language]
  );

  const formik = useFormik<ProfessionalFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      const phoneDigits = extractPhoneDigits(values.phone_display);

      const weeklyCommissionGoalTrimmed = values.weekly_commission_goal_display.trim();
      const weeklyAppointmentsGoalTrimmed = values.weekly_appointments_goal.trim();
      const monthlyCommissionGoalTrimmed = values.monthly_commission_goal_display.trim();
      const monthlyAppointmentsGoalTrimmed = values.monthly_appointments_goal.trim();

      const payload = {
        name: values.name.trim(),
        ...(useCommissionTracking
          ? { commission_rate_bps: percentToBps(values.commission_percent) }
          : {}),
        weekly_commission_goal_cents:
          weeklyCommissionGoalTrimmed === ""
            ? null
            : parseMoneyInputToCents(weeklyCommissionGoalTrimmed, i18n.language),
        weekly_appointments_goal:
          weeklyAppointmentsGoalTrimmed === ""
            ? null
            : Number.parseInt(weeklyAppointmentsGoalTrimmed, 10),
        monthly_commission_goal_cents:
          monthlyCommissionGoalTrimmed === ""
            ? null
            : parseMoneyInputToCents(monthlyCommissionGoalTrimmed, i18n.language),
        monthly_appointments_goal:
          monthlyAppointmentsGoalTrimmed === ""
            ? null
            : Number.parseInt(monthlyAppointmentsGoalTrimmed, 10),
        color_hex: normalizeHexColor(values.color_hex),
        email: values.email || null,
        ...(phoneDigits.length >= 10
          ? { country_code: "55", phone_number: phoneDigits }
          : { country_code: null, phone_number: null }),
      };

      try {
        if (editingProfessional) {
          const nextRateBps = useCommissionTracking
            ? percentToBps(values.commission_percent)
            : editingProfessional.commission_rate_bps;
          const rateChanged =
            useCommissionTracking &&
            nextRateBps !== editingProfessional.commission_rate_bps;
          const overrideTotal =
            editingProfessional.service_commission_overrides_total ??
            (editingProfessional.service_commission_overrides?.length ?? 0);

          if (rateChanged && overrideTotal > 0) {
            const confirmation = await Swal.fire({
              title: t("studio.professionals.serviceCommissions.confirmDefaultChangeTitle"),
              text: t("studio.professionals.serviceCommissions.confirmDefaultChangeText", {
                count: overrideTotal,
                percent: bpsToPercent(nextRateBps),
              }),
              icon: "info",
              showCancelButton: true,
              confirmButtonText: t(
                "studio.professionals.serviceCommissions.confirmDefaultChangeConfirm"
              ),
              cancelButtonText: t("professionals.actions.cancel"),
            });

            if (!confirmation.isConfirmed) {
              setSubmitting(false);
              return;
            }
          }

          const response = await updateProfessional(editingProfessional.id, payload);
          toast.success(response.message || t("professionals.toast.updateSuccess"));
          closeOffcanvas();
          await fetchProfessionals();
        } else {
          const response = await createProfessional(payload);
          toast.success(response.message || t("professionals.toast.createSuccess"));
          let created = response.data;

          if (created?.id && pendingPhotoFile) {
            try {
              const photoResponse = await uploadProfessionalPhoto(
                created.id,
                pendingPhotoFile
              );
              created = photoResponse.data ?? created;
              toast.success(
                photoResponse.message || t("professionals.toast.photoUploadSuccess")
              );
            } catch (photoError: any) {
              toast.error(
                photoError?.data || t("professionals.toast.photoUploadError")
              );
            }
          }

          clearPendingPhoto();
          closeOffcanvas();

          if (page !== 1) {
            setPage(1);
          } else {
            await fetchProfessionals();
          }

          if (created?.id) {
            let bookingUrl: string | null = null;
            try {
              const status = await getStatus();
              bookingUrl = status.data.quickstart?.booking_url ?? null;
            } catch {
              bookingUrl = null;
            }

            const nextStep = await Swal.fire({
              title: t("professionals.invite.afterCreateTitle", {
                name: created.name,
              }),
              text: t("professionals.invite.afterCreateText", {
                name: created.name,
              }),
              icon: "question",
              showDenyButton: true,
              showCancelButton: true,
              confirmButtonText: t("professionals.invite.afterCreateConfirm"),
              denyButtonText: t("professionals.invite.afterCreateInvite"),
              cancelButtonText: t("professionals.invite.afterCreateSkip"),
              confirmButtonColor: "#405189",
              denyButtonColor: "#0ab39c",
              cancelButtonColor: "#878a99",
              footer: bookingUrl
                ? `<button type="button" class="btn btn-link btn-sm p-0" id="adatis-copy-book">${t(
                    "professionals.invite.afterCreateCopyBook"
                  )}</button>`
                : undefined,
              didOpen: (popup) => {
                const copyBtn = popup.querySelector("#adatis-copy-book");
                if (!copyBtn || !bookingUrl) {
                  return;
                }
                copyBtn.addEventListener("click", () => {
                  void navigator.clipboard
                    .writeText(bookingUrl)
                    .then(() => {
                      toast.success(t("professionals.invite.copyBookSuccess"));
                    })
                    .catch(() => {
                      toast.error(t("professionals.invite.copyBookError"));
                    });
                });
              },
            });

            if (nextStep.isConfirmed) {
              openScheduleRef.current(created);
            } else if (nextStep.isDenied) {
              setInviteTarget(created);
              setInviteEmail(created.email || created.user?.email || "");
            }
          }
        }
      } catch (error: any) {
        const apiError = error as ProfessionalApiError;

        if (
          !applyProfessionalValidationErrors(
            apiError,
            formik.setFieldError,
            formik.setFieldTouched,
            t
          )
        ) {
          toast.error(resolveProfessionalSaveErrorMessage(apiError, t("professionals.toast.saveError")));
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handlePhoneBlur = () => {
    const digits = extractPhoneDigits(formik.values.phone_display);
    const normalized =
      digits.length === 0 ? "" : formatPhoneDisplay("55", digits);

    if (normalized !== formik.values.phone_display) {
      formik.setFieldValue("phone_display", normalized);
    }

    formik.setFieldTouched("phone_display", true);
  };

  const closeOffcanvas = () => {
    setOffcanvasOpen(false);
    setEditingProfessional(null);
    setFormLoading(false);
    setProfessionalFormTab("data");
    setEnablementData(null);
    setEnablementSearch("");
    setSelectedServiceIds([]);
    clearPendingPhoto();
  };

  const openCreate = () => {
    setEditingProfessional(null);
    setFormLoading(false);
    setProfessionalFormTab("data");
    clearPendingPhoto();
    setOffcanvasOpen(true);
  };

  const loadProfessionalEnablement = useCallback(async (professionalId: number) => {
    setEnablementLoading(true);

    try {
      const response = await getProfessionalServices(professionalId);
      setEnablementData(response.data);
      setSelectedServiceIds(
        response.data.services
          .filter((service) => service.is_enabled)
          .map((service) => service.id)
      );
    } catch (error: any) {
      toast.error(error?.data || t("studio.professionals.enablement.toast.loadError"));
    } finally {
      setEnablementLoading(false);
    }
  }, [t]);

  const saveProfessionalEnablement = async () => {
    if (!editingProfessional) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await syncProfessionalServices(
        editingProfessional.id,
        selectedServiceIds
      );
      toast.success(response.message || t("studio.professionals.enablement.toast.saveSuccess"));
      setEnablementData(response.data);
      const marked = (response.data.services ?? [])
        .filter((service) => service.is_enabled)
        .map((service) => ({ id: service.id, name: service.name }));
      patchLocalProfessional(editingProfessional.id, (current) => ({
        ...current,
        enabled_services: marked,
      }));
      markProfessionalRosterUpdated(editingProfessional.name, "services", editingProfessional.id);
    } catch (error: any) {
      toast.error(error?.data || t("studio.professionals.enablement.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEnablementService = (serviceId: number, checked: boolean) => {
    setSelectedServiceIds((current) => {
      if (checked) {
        return current.includes(serviceId) ? current : [...current, serviceId];
      }

      return current.filter((id) => id !== serviceId);
    });
  };

  const handleProfessionalFormTabChange = (tab: ProfessionalFormTab) => {
    setProfessionalFormTab(tab);
  };

  useEffect(() => {
    if (
      professionalFormTab !== "enablement" ||
      !restrictProfessionalServices ||
      !editingProfessional ||
      enablementData?.professional_id === editingProfessional.id
    ) {
      return;
    }

    void loadProfessionalEnablement(editingProfessional.id);
  }, [
    professionalFormTab,
    restrictProfessionalServices,
    editingProfessional,
    enablementData,
    loadProfessionalEnablement,
  ]);

  const openInvite = (professional: Professional) => {
    setInviteTarget(professional);
    setInviteEmail(professional.email || professional.user?.email || "");
  };

  const closeInvite = () => {
    setInviteTarget(null);
    setInviteEmail("");
    setInviteSubmitting(false);
  };

  const submitInvite = async () => {
    if (!inviteTarget) {
      return;
    }

    const email = inviteEmail.trim();

    if (!email) {
      toast.error(t("professionals.invite.validation.emailRequired"));
      return;
    }

    const isResend = resolveProfessionalAccessStatus(inviteTarget) === "pending";

    if (isResend) {
      const cooldownMs = getInviteResendCooldownRemainingMs(
        inviteTarget.invited_at,
        nowTick
      );
      if (cooldownMs > 0) {
        toast.info(
          t("professionals.invite.resendCooldown", {
            seconds: formatInviteCooldownSeconds(cooldownMs),
          })
        );
        return;
      }

      const confirmation = await Swal.fire({
        title: t("professionals.invite.resendConfirmTitle"),
        text: t("professionals.invite.resendConfirmText", {
          name: inviteTarget.name,
          email,
        }),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("professionals.invite.actions.resend"),
        cancelButtonText: t("professionals.actions.cancel"),
        confirmButtonColor: "#405189",
        cancelButtonColor: "#878a99",
        focusCancel: true,
      });

      if (!confirmation.isConfirmed) {
        return;
      }
    }

    setInviteSubmitting(true);

    try {
      const response = await inviteProfessional(inviteTarget.id, { email });
      toast.success(response.message || t("professionals.invite.success"));
      const inviteUrl = response.data?.invite_url ?? null;
      if (inviteUrl) {
        setInviteUrlByProfessionalId((previous) => ({
          ...previous,
          [inviteTarget.id]: inviteUrl,
        }));
      }
      const inviteeName = inviteTarget.name;
      const inviteePhoneDigits =
        inviteTarget.country_code && inviteTarget.phone_number
          ? `${String(inviteTarget.country_code).replace(/\D/g, "")}${String(
              inviteTarget.phone_number
            ).replace(/\D/g, "")}`
          : null;
      closeInvite();
      void fetchProfessionals();

      if (inviteUrl) {
        const shareChoice = await promptInviteShare({
          title: t("professionals.invite.copyLinkTitle"),
          text: t("professionals.invite.copyLinkText"),
          inviteUrl,
          whatsappMessage: t("professionals.invite.whatsappMessage", {
            name: inviteeName,
            url: inviteUrl,
          }),
          whatsappPhone: inviteePhoneDigits,
          labels: {
            copy: t("professionals.invite.copyLink"),
            whatsapp: t("professionals.invite.whatsapp"),
            sms: t("professionals.invite.sms"),
            share: t("professionals.invite.share"),
            done: t("professionals.invite.copyLinkSkip"),
          },
        });

        if (shareChoice === "copy") {
          const copied = await handleInviteShareChoice(shareChoice, inviteUrl, {
            copySuccess: t("professionals.invite.copyLinkSuccess"),
            copyError: t("professionals.invite.copyLinkError"),
          });
          toast[copied ? "success" : "error"](
            copied
              ? t("professionals.invite.copyLinkSuccess")
              : t("professionals.invite.copyLinkError")
          );
        }
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.data ||
          t("professionals.invite.error")
      );
    } finally {
      setInviteSubmitting(false);
    }
  };

  const openEdit = async (professional: Professional, initialTab: ProfessionalFormTab = "data") => {
    setEditingProfessional(null);
    clearPendingPhoto();
    setFormLoading(true);
    setProfessionalFormTab(initialTab);
    setEnablementData(null);
    setEnablementSearch("");
    setSelectedServiceIds([]);
    setOffcanvasOpen(true);

    try {
      const response = await getProfessional(professional.id);
      setEditingProfessional(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("professionals.toast.loadError"));
      closeOffcanvas();
    } finally {
      setFormLoading(false);
    }
  };

  openEditRef.current = openEdit;

  const openCommissions = (professional: Professional) => {
    void openEdit(professional, "commissions");
  };

  useEffect(() => {
    if (deepLinkHandledRef.current || professionals.length === 0) {
      return;
    }

    const openId = Number(searchParams.get("open"));
    const scheduleId = Number(searchParams.get("schedule"));
    const tabParam = searchParams.get("tab");

    if (Number.isFinite(scheduleId) && scheduleId >= 1) {
      const professional = professionals.find((item) => item.id === scheduleId);

      if (!professional) {
        return;
      }

      deepLinkHandledRef.current = true;
      openSchedule(professional);
      window.setTimeout(() => {
        document
          .getElementById(`professional-row-${scheduleId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);

      const next = new URLSearchParams(searchParams);
      next.delete("schedule");
      setSearchParams(next, { replace: true });
      return;
    }

    if (!Number.isFinite(openId) || openId < 1) {
      return;
    }

    const professional = professionals.find((item) => item.id === openId);

    if (!professional) {
      return;
    }

    const initialTab: ProfessionalFormTab =
      tabParam === "commissions" || tabParam === "enablement" || tabParam === "data"
        ? tabParam
        : "data";

    deepLinkHandledRef.current = true;
    void openEdit(professional, initialTab);
    window.setTimeout(() => {
      document
        .getElementById(`professional-row-${openId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);

    const next = new URLSearchParams(searchParams);
    next.delete("open");
    next.delete("tab");
    setSearchParams(next, { replace: true });
    // openEdit is stable for this one-shot deep-link; including it re-runs after every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionals, searchParams, setSearchParams]);

  const confirmDelete = async (professional: Professional) => {
    const confirmation = await Swal.fire({
      title: t("professionals.delete.permanent.title"),
      html: `
        <p class="text-muted mb-2">${t("professionals.delete.permanent.description", { name: professional.name })}</p>
        <p class="text-danger fw-semibold mb-0">${t("professionals.delete.permanent.hint")}</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("professionals.delete.permanent.confirm"),
      cancelButtonText: t("professionals.actions.cancel"),
      confirmButtonColor: "#f06548",
      cancelButtonColor: "#878a99",
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setActionTargetId(professional.id);

    try {
      const response = await deleteProfessional(professional.id);
      toast.success(response.message || t("professionals.toast.deleteSuccess"));
      removeLocalProfessional(professional.id);

      const isLastItemOnPage = professionals.length === 1 && page > 1;
      if (isLastItemOnPage) {
        setPage((current) => current - 1);
      } else {
        await fetchProfessionals();
      }
    } catch (error: any) {
      toast.error(error?.data || t("professionals.toast.deleteError"));
    } finally {
      setActionTargetId(null);
    }
  };

  const handleDeactivate = async (professional: Professional) => {
    const confirmation = await Swal.fire({
      title: t("professionals.archive.confirm.title"),
      text: t("professionals.archive.confirm.description", { name: professional.name }),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("professionals.archive.confirm.confirmButton"),
      cancelButtonText: t("professionals.actions.cancel"),
      confirmButtonColor: "#f7b84b",
      cancelButtonColor: "#878a99",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setActionTargetId(professional.id);

    try {
      const response = await deactivateProfessional(professional.id);
      toast.success(response.message || t("professionals.toast.archiveSuccess"));

      if (showArchived) {
        patchLocalProfessional(professional.id, () => response.data);
      } else {
        removeLocalProfessional(professional.id);
      }

      if (editingProfessional?.id === professional.id) {
        setEditingProfessional(response.data);
      }
    } catch (error: any) {
      const message =
        error?.code === FUTURE_APPOINTMENTS_ERROR_CODE
          ? error?.data || t("professionals.toast.futureAppointmentsError")
          : error?.data || t("professionals.toast.archiveError");
      toast.error(message);
    } finally {
      setActionTargetId(null);
    }
  };

  const handleRestore = async (professional: Professional) => {
    setActionTargetId(professional.id);

    try {
      const response = await restoreProfessional(professional.id);
      toast.success(response.message || t("professionals.toast.restoreSuccess"));

      if (showArchived) {
        patchLocalProfessional(professional.id, () => response.data);
      } else {
        removeLocalProfessional(professional.id);
      }

      if (editingProfessional?.id === professional.id) {
        setEditingProfessional(response.data);
      }
    } catch (error: any) {
      toast.error(error?.data || t("professionals.toast.restoreError"));
    } finally {
      setActionTargetId(null);
    }
  };

  const openSchedule = (professional: Professional) => {
    setScheduleTarget(professional);
    setScheduleModalOpen(true);
  };

  openScheduleRef.current = openSchedule;

  const closeSchedule = () => {
    setScheduleModalOpen(false);
    setScheduleTarget(null);
  };

  const openAbsences = (professional: Professional) => {
    setAbsencesTarget(professional);
    setAbsencesOpen(true);
  };

  const closeAbsences = () => {
    setAbsencesOpen(false);
    setAbsencesTarget(null);
  };

  const totalPages = meta?.last_page ?? 1;
  const hasActiveSearch = debouncedSearch.length > 0;
  const notAvailable = t("professionals.common.notAvailable");

  const formatPhoneLabel = (professional: Professional): string => {
    if (!professional.phone_number) {
      return notAvailable;
    }

    const display = formatPhoneDisplay(professional.country_code, professional.phone_number);
    return professional.country_code === "55" || !professional.country_code
      ? display
      : `+${professional.country_code} ${display}`;
  };

  const filteredEnablementServices = useMemo(() => {
    if (!enablementData) {
      return [];
    }

    const term = enablementSearch.trim().toLowerCase();

    return enablementData.services.filter((service) =>
      term === "" ? true : service.name.toLowerCase().includes(term)
    );
  }, [enablementData, enablementSearch]);

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("professionals.breadcrumb.title")}
            pageTitle={t("professionals.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <h4 className="card-title mb-0 flex-grow-1">{t("professionals.card.title")}</h4>
                  <HelpArticleLink articlePath={HELP_ARTICLE_PROFESSIONALS} className="align-self-center" />
                  <HelpArticleLink articlePath={HELP_ARTICLE_INVITE_TEAM} className="align-self-center" />
                  <Button color="primary" onClick={openCreate}>
                    <i className="ri-add-line align-bottom me-1" />
                    {t("professionals.actions.new")}
                  </Button>
                </CardHeader>

                <CardBody>
                  {!useCommissionTracking ? (
                    <Alert color="info" className="mb-3">
                      <p className="mb-2">{t("professionals.commissionTrackingDisabled.banner")}</p>
                      <Link to="/settings/operation" className="alert-link">
                        {t("professionals.commissionTrackingDisabled.settingsLink")}
                      </Link>
                    </Alert>
                  ) : null}

                  {shouldShowPhotoWarningBanner ? (
                    <Alert color="warning" className="mb-3">
                      {t("studio.professionals.warnings.noPhotosForOnlineBooking")}
                    </Alert>
                  ) : null}

                  <Row className="mb-3 align-items-center g-2">
                    <Col md={5} lg={4}>
                      <div className="search-box">
                        <Input
                          type="text"
                          className="form-control"
                          placeholder={t("professionals.search.placeholder")}
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                        />
                        <i className="ri-search-line search-icon" />
                      </div>
                    </Col>
                    <Col md="auto">
                      <div className="form-check form-switch mb-0">
                        <Input
                          id="professionals-show-archived"
                          type="checkbox"
                          role="switch"
                          className="form-check-input"
                          checked={showArchived}
                          onChange={(event) => setShowArchived(event.target.checked)}
                        />
                        <Label className="form-check-label" htmlFor="professionals-show-archived">
                          {t("professionals.filters.showArchived")}
                        </Label>
                      </div>
                    </Col>
                    {useCommissionTracking ? (
                      <Col md="auto">
                        <div className="form-check form-switch mb-0">
                          <Input
                            id="professionals-only-service-commissions"
                            type="checkbox"
                            role="switch"
                            className="form-check-input"
                            checked={onlyServiceCommissions}
                            onChange={(event) =>
                              setOnlyServiceCommissions(event.target.checked)
                            }
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="professionals-only-service-commissions"
                          >
                            {t("professionals.filters.onlyServiceCommissions")}
                          </Label>
                        </div>
                      </Col>
                    ) : null}
                  </Row>

                  {serviceCommissionFocusId ? (
                    <Alert color="info" className="mb-3">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <span>
                          {t("studio.professionals.serviceCommissions.focusBanner", {
                            name:
                              serviceCommissionFocusName ||
                              t("studio.professionals.serviceCommissions.focusBannerFallback"),
                          })}
                        </span>
                        <Button
                          color="link"
                          className="p-0"
                          type="button"
                          onClick={() => {
                            const next = new URLSearchParams(searchParams);
                            next.delete("service_commission");
                            next.delete("service_commission_name");
                            setSearchParams(next, { replace: true });
                          }}
                        >
                          {t("studio.professionals.serviceCommissions.focusBannerDismiss")}
                        </Button>
                      </div>
                    </Alert>
                  ) : null}

                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary">{t("professionals.loading")}</Spinner>
                    </div>
                  ) : professionals.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-3">
                        {hasActiveSearch
                          ? t("professionals.empty.noResults", { term: debouncedSearch })
                          : t("professionals.empty.noData")}
                      </p>
                      {!hasActiveSearch && (
                        <Button color="soft-primary" onClick={openCreate}>
                          {t("professionals.actions.createFirst")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table hover className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("professionals.table.name")}</th>
                              <th>{t("professionals.table.email")}</th>
                              <th>{t("professionals.table.phone")}</th>
                              {useCommissionTracking ? (
                                <th>{t("professionals.table.commission")}</th>
                              ) : null}
                              <th>{t("professionals.table.color")}</th>
                              <th>{t("professionals.table.status")}</th>
                              <th className="text-end" style={{ width: 188 }}>
                                {t("professionals.table.actions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {professionals.map((professional) => {
                              const active = isProfessionalActive(professional);
                              const rowBusy = actionTargetId === professional.id;
                              const accessStatus = resolveProfessionalAccessStatus(professional);
                              const canInvite = active && accessStatus !== "active";
                              const isResendInvite = accessStatus === "pending";
                              const inviteSentAtLabel = formatInviteSentAt(
                                professional.invited_at,
                                i18n.language
                              );
                              const invitePendingTitle = inviteSentAtLabel
                                ? t("professionals.invite.sentAt", {
                                    date: inviteSentAtLabel,
                                  })
                                : undefined;
                              const resendCooldownMs = isResendInvite
                                ? getInviteResendCooldownRemainingMs(
                                    professional.invited_at,
                                    nowTick
                                  )
                                : 0;
                              const resendCooldownSeconds =
                                formatInviteCooldownSeconds(resendCooldownMs);
                              const inviteButtonTitle =
                                isResendInvite && resendCooldownMs > 0
                                  ? t("professionals.invite.resendCooldown", {
                                      seconds: resendCooldownSeconds,
                                    })
                                  : isResendInvite
                                    ? t("professionals.invite.actions.resend")
                                    : t("professionals.cta_invite.professional");

                              return (
                              <tr
                                key={professional.id}
                                id={`professional-row-${professional.id}`}
                                className={!active ? "table-light" : undefined}
                              >
                                <td style={{ whiteSpace: "normal" }}>
                                  <div className="d-flex align-items-start gap-2">
                                    <ProfessionalAvatar
                                      name={professional.name}
                                      colorHex={professional.color_hex}
                                      photoUrl={resolvePublicStorageUrl(professional.photo_path)}
                                      size={36}
                                    />
                                    <div>
                                      <div className="d-flex flex-wrap align-items-center gap-1">
                                    <span className={`fw-medium${!active ? " text-muted" : ""}`}>
                                      {professional.name}
                                    </span>
                                    {accessStatus === "active" ? (
                                      <Badge
                                        color="success"
                                        className="badge-soft-success ms-1 align-middle"
                                      >
                                        {t("professionals.badges.has_access")}
                                      </Badge>
                                    ) : accessStatus === "pending" ? (
                                      <Badge
                                        color="warning"
                                        className="badge-soft-warning ms-1 align-middle"
                                        title={invitePendingTitle}
                                      >
                                        {t("professionals.badges.invite_pending")}
                                      </Badge>
                                    ) : (
                                      <Badge
                                        color="secondary"
                                        className="badge-soft-secondary ms-1 align-middle"
                                      >
                                        {t("professionals.badges.no_access")}
                                      </Badge>
                                    )}
                                    {accessStatus === "pending" &&
                                    inviteUrlByProfessionalId[professional.id] ? (
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0 ms-1 align-middle"
                                        title={t("professionals.invite.copyLink")}
                                        onClick={async () => {
                                          const url =
                                            inviteUrlByProfessionalId[professional.id];
                                          const copied = await handleInviteShareChoice(
                                            "copy",
                                            url,
                                            {
                                              copySuccess: t(
                                                "professionals.invite.copyLinkSuccess"
                                              ),
                                              copyError: t(
                                                "professionals.invite.copyLinkError"
                                              ),
                                            }
                                          );
                                          toast[copied ? "success" : "error"](
                                            copied
                                              ? t("professionals.invite.copyLinkSuccess")
                                              : t("professionals.invite.copyLinkError")
                                          );
                                        }}
                                      >
                                        <i className="ri-link" />
                                        <span className="visually-hidden">
                                          {t("professionals.invite.copyLink")}
                                        </span>
                                      </Button>
                                    ) : null}
                                      </div>
                                      <ProfessionalRosterLines
                                        services={professional.enabled_services}
                                        schedules={professional.schedules}
                                        restrictProfessionalServices={restrictProfessionalServices}
                                        onMarkServices={() => {
                                          void openEdit(professional, "enablement");
                                        }}
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td className="text-muted">{professional.email ?? notAvailable}</td>
                                <td>{formatPhoneLabel(professional)}</td>
                                {useCommissionTracking ? (
                                  <td>
                                    <div>{bpsToPercent(professional.commission_rate_bps)}%</div>
                                    {(() => {
                                      const preview = formatServiceCommissionPreview(
                                        professional,
                                        t
                                      );
                                      if (!preview) {
                                        return null;
                                      }

                                      return (
                                        <button
                                          type="button"
                                          className="btn btn-link p-0 text-start small text-muted text-decoration-none"
                                          onClick={() => void openEdit(professional, "data")}
                                          title={preview}
                                        >
                                          {preview}
                                        </button>
                                      );
                                    })()}
                                  </td>
                                ) : null}
                                <td>
                                  <span
                                    className="badge rounded-pill"
                                    style={{
                                      backgroundColor: normalizeHexColor(professional.color_hex),
                                      color: "#fff",
                                      minWidth: 72,
                                      opacity: active ? 1 : 0.55,
                                    }}
                                  >
                                    {normalizeHexColor(professional.color_hex)}
                                  </span>
                                </td>
                                <td>
                                  {active ? (
                                    <Badge color="success" className="badge-soft-success">
                                      {t("professionals.badges.active")}
                                    </Badge>
                                  ) : (
                                    <Badge color="warning" className="badge-soft-warning">
                                      {t("professionals.badges.archived")}
                                    </Badge>
                                  )}
                                </td>
                                <td className="text-end">
                                  <Button
                                    color="soft-warning"
                                    size="sm"
                                    className="me-1"
                                    title={t("professionals.actions.absences")}
                                    disabled={rowBusy}
                                    onClick={() => openAbsences(professional)}
                                  >
                                    <i className="ri-calendar-close-line" />
                                  </Button>
                                  <Button
                                    color="soft-info"
                                    size="sm"
                                    className="me-1"
                                    title={t("professionals.actions.scheduleTooltip")}
                                    aria-label={t("professionals.actions.scheduleTooltip")}
                                    disabled={rowBusy}
                                    onClick={() => openSchedule(professional)}
                                  >
                                    <i className="ri-calendar-schedule-line" />
                                  </Button>
                                  {useCommissionTracking ? (
                                    <Button
                                      color="soft-success"
                                      size="sm"
                                      className="me-1"
                                      title={t("professionals.actions.commissionsTooltip")}
                                      aria-label={t("professionals.actions.commissionsTooltip")}
                                      disabled={rowBusy}
                                      onClick={() => openCommissions(professional)}
                                    >
                                      <i className="ri-money-dollar-circle-line" />
                                    </Button>
                                  ) : null}
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    className="me-1"
                                    title={t("professionals.actions.edit")}
                                    disabled={rowBusy}
                                    onClick={() => openEdit(professional)}
                                  >
                                    <i className="ri-pencil-line" />
                                  </Button>
                                  {canInvite ? (
                                    <Button
                                      color="soft-success"
                                      size="sm"
                                      className="me-1"
                                      title={inviteButtonTitle}
                                      disabled={rowBusy || resendCooldownMs > 0}
                                      onClick={() => openInvite(professional)}
                                    >
                                      {isResendInvite && resendCooldownMs > 0 ? (
                                        <span className="px-1">{resendCooldownSeconds}s</span>
                                      ) : (
                                        <i className="ri-mail-send-line" />
                                      )}
                                    </Button>
                                  ) : null}
                                  <UncontrolledDropdown direction="start">
                                    <DropdownToggle
                                      color="soft-secondary"
                                      size="sm"
                                      className="btn-icon"
                                      disabled={rowBusy}
                                      aria-label={t("professionals.actions.moreActions")}
                                    >
                                      {rowBusy ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        <i className="ri-more-2-fill" />
                                      )}
                                    </DropdownToggle>
                                    <DropdownMenu end>
                                      {active ? (
                                        <DropdownItem
                                          onClick={() => {
                                            void handleDeactivate(professional);
                                          }}
                                        >
                                          <i className="ri-user-unfollow-line align-bottom me-2 text-warning" />
                                          {t("professionals.actions.deactivate")}
                                        </DropdownItem>
                                      ) : (
                                        <DropdownItem
                                          onClick={() => {
                                            void handleRestore(professional);
                                          }}
                                        >
                                          <i className="ri-user-follow-line align-bottom me-2 text-success" />
                                          {t("professionals.actions.restore")}
                                        </DropdownItem>
                                      )}
                                      <DropdownItem divider />
                                      <DropdownItem
                                        className="text-danger"
                                        onClick={() => {
                                          void confirmDelete(professional);
                                        }}
                                      >
                                        <i className="ri-delete-bin-line align-bottom me-2" />
                                        {t("professionals.actions.deletePermanent")}
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </td>
                              </tr>
                            );
                            })}
                          </tbody>
                        </Table>
                      </div>

                      {meta && totalPages > 1 && (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("professionals.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("professionals.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("professionals.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={closeOffcanvas}
        direction="end"
        className="border-0"
      >
        <OffcanvasHeader toggle={closeOffcanvas} className="bg-light">
          {editingProfessional
            ? t("professionals.form.editTitle")
            : t("professionals.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("clients.loading")}</Spinner>
            </div>
          ) : (
            <>
              {editingProfessional && !isProfessionalActive(editingProfessional) ? (
                <Alert color="warning" className="mb-4">
                  <i className="ri-archive-line align-bottom me-1" />
                  {t("professionals.offcanvas.archivedBanner")}
                </Alert>
              ) : null}

              {editingProfessional ? (
                <Nav pills className="nav-pills-custom nav-primary gap-2 mb-4">
                  <NavItem>
                    <NavLink
                      href="#"
                      className={professionalFormTab === "data" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        handleProfessionalFormTabChange("data");
                      }}
                    >
                      {t("studio.professionals.form.tabs.data")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={professionalFormTab === "enablement" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        handleProfessionalFormTabChange("enablement");
                      }}
                    >
                      {t("studio.professionals.form.tabs.enablement")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={professionalFormTab === "commissions" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        handleProfessionalFormTabChange("commissions");
                      }}
                    >
                      {t("studio.professionals.form.tabs.commissions")}
                    </NavLink>
                  </NavItem>
                </Nav>
              ) : null}

              {professionalFormTab === "data" || !editingProfessional ? (
          <Form
            key={entityFormKey(editingProfessional?.id)}
            onSubmit={(event) => {
              event.preventDefault();
              formik.handleSubmit();
            }}
          >
            <div className="mb-3">
              <Label htmlFor="professional-name">{t("professionals.fields.name.label")}</Label>
              <Input
                id="professional-name"
                name="name"
                placeholder={t("professionals.fields.name.placeholder")}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.name && formik.errors.name)}
              />
              {formik.touched.name && formik.errors.name ? (
                <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
              ) : null}
            </div>

            <ProfessionalPhotoField
              name={formik.values.name}
              colorHex={formik.values.color_hex}
              photoUrl={
                editingProfessional
                  ? resolvePublicStorageUrl(editingProfessional.photo_path)
                  : pendingPhotoPreview
              }
              busy={photoBusy}
              onPick={(file) => void handlePhotoPick(file)}
              onClear={() => void handlePhotoClear()}
              canClear={
                editingProfessional
                  ? Boolean(editingProfessional.photo_path)
                  : Boolean(pendingPhotoFile)
              }
              cropMemoryKey={editingProfessional?.id ?? null}
            />

            <div className="mb-3">
              <Label htmlFor="professional-email">{t("professionals.fields.email.label")}</Label>
              <Input
                id="professional-email"
                name="email"
                type="email"
                placeholder={t("professionals.fields.email.placeholder")}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.email && formik.errors.email)}
              />
              {formik.touched.email && formik.errors.email ? (
                <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="professional-phone">{t("professionals.fields.phone.label")}</Label>
              <Cleave
                id="professional-phone"
                className={`form-control${
                  formik.touched.phone_display && formik.errors.phone_display
                    ? " is-invalid"
                    : ""
                }`}
                placeholder={t("professionals.fields.phone.placeholder")}
                options={{
                  delimiters: ["(", ") ", "-"],
                  blocks: [0, 2, 5, 4],
                  numericOnly: true,
                }}
                value={formik.values.phone_display}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  formik.setFieldValue("phone_display", event.target.value)
                }
                onBlur={handlePhoneBlur}
              />
              <FormText>{t("professionals.fields.phone.optionalHint")}</FormText>
              {formik.touched.phone_display && formik.errors.phone_display ? (
                <div className="invalid-feedback d-block">
                  {formik.errors.phone_display}
                </div>
              ) : null}
            </div>

            {useCommissionTracking ? (
              <div className="mb-3">
                <Label htmlFor="professional-commission">
                  {t("professionals.fields.commission.label")}
                </Label>
                <InputGroup>
                  <Input
                    id="professional-commission"
                    name="commission_percent"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder={t("professionals.fields.commission.placeholder")}
                    value={formik.values.commission_percent}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      !!(formik.touched.commission_percent && formik.errors.commission_percent)
                    }
                  />
                  <InputGroupText>%</InputGroupText>
                </InputGroup>
                {formik.touched.commission_percent && formik.errors.commission_percent ? (
                  <FormFeedback type="invalid" className="d-block">
                    {formik.errors.commission_percent}
                  </FormFeedback>
                ) : null}
                {editingProfessional ? (
                  <ProfessionalServiceCommissionPanel
                    professionalId={editingProfessional.id}
                    defaultCommissionPercent={formik.values.commission_percent}
                    preferredServiceId={serviceCommissionFocusId}
                    onOverridesSaved={(overrides: ProfessionalServiceCommissionOverride[]) => {
                      setEditingProfessional((current) =>
                        current
                          ? {
                              ...current,
                              service_commission_overrides: overrides.slice(0, 3),
                              service_commission_overrides_total: overrides.length,
                            }
                          : current
                      );
                      setProfessionals((current) =>
                        current.map((row) =>
                          row.id === editingProfessional.id
                            ? {
                                ...row,
                                service_commission_overrides: overrides.slice(0, 3),
                                service_commission_overrides_total: overrides.length,
                              }
                            : row
                        )
                      );
                    }}
                  />
                ) : (
                  <p className="text-muted small mt-2 mb-0">
                    {t("studio.professionals.serviceCommissions.saveFirstHint")}
                  </p>
                )}
              </div>
            ) : null}

            {editingProfessional ? (
              <div className="mb-3">
                <Button
                  color="light"
                  type="button"
                  size="sm"
                  onClick={() => {
                    void (async () => {
                      try {
                        const response = await getProfessionalGoals(editingProfessional.id);
                        const suggestions = response.data.suggestions;
                        if (
                          !suggestions.weekly_commission_goal_cents &&
                          !suggestions.weekly_appointments_goal &&
                          !suggestions.monthly_commission_goal_cents &&
                          !suggestions.monthly_appointments_goal
                        ) {
                          toast.info(t("professionals.fields.goalsSuggest.empty"));
                          return;
                        }

                        if (suggestions.weekly_commission_goal_cents != null) {
                          formik.setFieldValue(
                            "weekly_commission_goal_display",
                            formatCentsToMoneyInput(
                              suggestions.weekly_commission_goal_cents,
                              i18n.language
                            )
                          );
                        }
                        if (suggestions.weekly_appointments_goal != null) {
                          formik.setFieldValue(
                            "weekly_appointments_goal",
                            String(suggestions.weekly_appointments_goal)
                          );
                        }
                        if (suggestions.monthly_commission_goal_cents != null) {
                          formik.setFieldValue(
                            "monthly_commission_goal_display",
                            formatCentsToMoneyInput(
                              suggestions.monthly_commission_goal_cents,
                              i18n.language
                            )
                          );
                        }
                        if (suggestions.monthly_appointments_goal != null) {
                          formik.setFieldValue(
                            "monthly_appointments_goal",
                            String(suggestions.monthly_appointments_goal)
                          );
                        }
                        toast.success(
                          t("professionals.fields.goalsSuggest.applied", {
                            weeks: suggestions.based_on_weeks,
                          })
                        );
                      } catch (error: any) {
                        toast.error(
                          error?.data || t("professionals.fields.goalsSuggest.error")
                        );
                      }
                    })();
                  }}
                >
                  <i className="ri-magic-line align-bottom me-1" />
                  {t("professionals.fields.goalsSuggest.button")}
                </Button>
                <FormText className="d-block">
                  {t("professionals.fields.goalsSuggest.hint")}
                </FormText>
              </div>
            ) : null}

            <div className="mb-3">
              <Label htmlFor="professional-weekly-commission-goal">
                {t("professionals.fields.weeklyCommissionGoal.label")}
              </Label>
              <Input
                id="professional-weekly-commission-goal"
                name="weekly_commission_goal_display"
                placeholder={t("professionals.fields.weeklyCommissionGoal.placeholder")}
                value={formik.values.weekly_commission_goal_display}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  !!(
                    formik.touched.weekly_commission_goal_display &&
                    formik.errors.weekly_commission_goal_display
                  )
                }
              />
              <FormText>{t("professionals.fields.weeklyCommissionGoal.hint")}</FormText>
              {formik.touched.weekly_commission_goal_display &&
              formik.errors.weekly_commission_goal_display ? (
                <FormFeedback type="invalid" className="d-block">
                  {formik.errors.weekly_commission_goal_display}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="professional-weekly-appointments-goal">
                {t("professionals.fields.weeklyAppointmentsGoal.label")}
              </Label>
              <Input
                id="professional-weekly-appointments-goal"
                name="weekly_appointments_goal"
                type="number"
                min="0"
                max="500"
                placeholder={t("professionals.fields.weeklyAppointmentsGoal.placeholder")}
                value={formik.values.weekly_appointments_goal}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  !!(
                    formik.touched.weekly_appointments_goal &&
                    formik.errors.weekly_appointments_goal
                  )
                }
              />
              <FormText>{t("professionals.fields.weeklyAppointmentsGoal.hint")}</FormText>
              {formik.touched.weekly_appointments_goal &&
              formik.errors.weekly_appointments_goal ? (
                <FormFeedback type="invalid" className="d-block">
                  {formik.errors.weekly_appointments_goal}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="professional-monthly-commission-goal">
                {t("professionals.fields.monthlyCommissionGoal.label")}
              </Label>
              <Input
                id="professional-monthly-commission-goal"
                name="monthly_commission_goal_display"
                placeholder={t("professionals.fields.monthlyCommissionGoal.placeholder")}
                value={formik.values.monthly_commission_goal_display}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  !!(
                    formik.touched.monthly_commission_goal_display &&
                    formik.errors.monthly_commission_goal_display
                  )
                }
              />
              <FormText>{t("professionals.fields.monthlyCommissionGoal.hint")}</FormText>
              {formik.touched.monthly_commission_goal_display &&
              formik.errors.monthly_commission_goal_display ? (
                <FormFeedback type="invalid" className="d-block">
                  {formik.errors.monthly_commission_goal_display}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="professional-monthly-appointments-goal">
                {t("professionals.fields.monthlyAppointmentsGoal.label")}
              </Label>
              <Input
                id="professional-monthly-appointments-goal"
                name="monthly_appointments_goal"
                type="number"
                min="0"
                max="2000"
                placeholder={t("professionals.fields.monthlyAppointmentsGoal.placeholder")}
                value={formik.values.monthly_appointments_goal}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  !!(
                    formik.touched.monthly_appointments_goal &&
                    formik.errors.monthly_appointments_goal
                  )
                }
              />
              <FormText>{t("professionals.fields.monthlyAppointmentsGoal.hint")}</FormText>
              {formik.touched.monthly_appointments_goal &&
              formik.errors.monthly_appointments_goal ? (
                <FormFeedback type="invalid" className="d-block">
                  {formik.errors.monthly_appointments_goal}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="professional-color">{t("professionals.fields.color.label")}</Label>
              <div className="d-flex gap-2 align-items-center">
                <Input
                  type="color"
                  title={t("professionals.fields.color.selectTitle")}
                  value={normalizeHexColor(formik.values.color_hex)}
                  onChange={(event) =>
                    formik.setFieldValue("color_hex", event.target.value.toUpperCase())
                  }
                  style={{ width: 48, height: 38, padding: 4 }}
                />
                <Input
                  id="professional-color"
                  name="color_hex"
                  placeholder={t("professionals.fields.color.placeholder")}
                  value={formik.values.color_hex}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.color_hex && formik.errors.color_hex)}
                />
              </div>
              {formik.touched.color_hex && formik.errors.color_hex ? (
                <FormFeedback type="invalid" className="d-block">
                  {formik.errors.color_hex}
                </FormFeedback>
              ) : null}
            </div>

            {!editingProfessional ? (
              restrictProfessionalServices ? (
                <p className="text-muted mb-3">
                  {t("studio.professionals.specialty.markAfterSave")}
                </p>
              ) : (
                <ServiceCoverageNotice
                  message={t("studio.professionals.specialty.allServices")}
                  showSettingsLink={canManageServiceCoverage}
                />
              )
            ) : null}

            <div className="d-flex gap-2 mt-4">
              <Button color="light" type="button" onClick={closeOffcanvas}>
                {t("professionals.actions.cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="ri-save-line align-bottom me-1" />
                )}
                {t("professionals.actions.save")}
              </Button>
            </div>
          </Form>
              ) : professionalFormTab === "enablement" ? (
                <div>
                  {!restrictProfessionalServices ? (
                    <ServiceCoverageNotice
                      message={t("studio.professionals.specialty.allServices")}
                      showSettingsLink={canManageServiceCoverage}
                    />
                  ) : enablementLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary">{t("professionals.loading")}</Spinner>
                    </div>
                  ) : (
                    <>
                      <p className="text-muted small mb-3">
                        {t("studio.professionals.enablement.description")}
                      </p>
                      <Input
                        type="search"
                        className="mb-3"
                        placeholder={t("studio.professionals.enablement.searchPlaceholder")}
                        value={enablementSearch}
                        onChange={(event) => setEnablementSearch(event.target.value)}
                      />
                      {filteredEnablementServices.length === 0 ? (
                        <p className="text-muted">{t("studio.professionals.enablement.empty")}</p>
                      ) : (
                        <div className="d-flex flex-column gap-2 mb-3">
                          {filteredEnablementServices.map((service) => (
                            <div className="form-check" key={service.id}>
                              <Input
                                id={`enablement-service-${service.id}`}
                                type="checkbox"
                                checked={selectedServiceIds.includes(service.id)}
                                onChange={(event) =>
                                  toggleEnablementService(service.id, event.target.checked)
                                }
                              />
                              <Label
                                className="form-check-label"
                                htmlFor={`enablement-service-${service.id}`}
                              >
                                {service.name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="d-flex gap-2 justify-content-end">
                        <Button color="light" type="button" onClick={closeOffcanvas}>
                          {t("professionals.actions.cancel")}
                        </Button>
                        <Button
                          color="primary"
                          type="button"
                          onClick={saveProfessionalEnablement}
                          disabled={submitting}
                        >
                          {submitting
                            ? t("professionals.loading")
                            : t("studio.professionals.enablement.actions.save")}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ) : professionalFormTab === "commissions" ? (
                <>
                  {useCommissionTracking ? (
                    <Alert color="light" className="border mb-3">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div>
                          <div className="fw-medium">
                            {t("studio.professionals.serviceCommissions.title")}
                          </div>
                          <div className="small text-muted">
                            {(editingProfessional.service_commission_overrides_total ?? 0) > 0
                              ? formatServiceCommissionPreview(editingProfessional, t)
                              : t("studio.professionals.serviceCommissions.empty", {
                                  percent: bpsToPercent(
                                    editingProfessional.commission_rate_bps
                                  ),
                                })}
                          </div>
                        </div>
                        <Button
                          color="soft-primary"
                          size="sm"
                          type="button"
                          onClick={() => {
                            handleProfessionalFormTabChange("data");
                            window.setTimeout(() => {
                              document
                                .getElementById("professional-service-commissions")
                                ?.scrollIntoView({ behavior: "smooth", block: "start" });
                            }, 80);
                          }}
                        >
                          {t("studio.professionals.serviceCommissions.actions.editFromCommissions")}
                        </Button>
                      </div>
                    </Alert>
                  ) : null}
                  <ProfessionalGoalsProgressPanel professionalId={editingProfessional.id} />
                  {useCommissionTracking ? (
                    <ProfessionalCommissionSummaryPanel
                      professionalId={editingProfessional.id}
                      professionalName={editingProfessional.name}
                    />
                  ) : (
                    <Alert color="light" className="border mb-3">
                      <div className="fw-medium mb-1">
                        {t("studio.professionals.commissions.trackingOff.title")}
                      </div>
                      <p className="text-muted small mb-0">
                        {t("studio.professionals.commissions.trackingOff.body")}
                      </p>
                    </Alert>
                  )}
                  <div className="d-flex justify-content-end mt-4">
                    <Button color="light" type="button" onClick={closeOffcanvas}>
                      {t("professionals.actions.cancel")}
                    </Button>
                  </div>
                </>
              ) : null}
            </>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <ProfessionalScheduleModal
        professional={scheduleTarget}
        isOpen={scheduleModalOpen}
        onClose={closeSchedule}
        onSaved={(schedules) => {
          if (!scheduleTarget) {
            return;
          }

          patchLocalProfessional(scheduleTarget.id, (current) => ({
            ...current,
            schedules: schedules.map((slot) => {
              const label = slot.break_label?.trim() ?? "";

              return {
                day_of_week: slot.day_of_week,
                start_time: slot.start_time.slice(0, 5),
                end_time: slot.end_time.slice(0, 5),
                ...(label ? { break_label: label.slice(0, 40) } : {}),
              };
            }),
          }));
          markProfessionalRosterUpdated(scheduleTarget.name, "routine", scheduleTarget.id);
        }}
      />

      <ScheduleBlocksModal
        professional={absencesTarget}
        isOpen={absencesOpen}
        onClose={closeAbsences}
      />

      <Modal isOpen={!!inviteTarget} toggle={closeInvite} centered>
        <ModalHeader toggle={closeInvite}>
          {inviteTarget && resolveProfessionalAccessStatus(inviteTarget) === "pending"
            ? t("professionals.invite.resendTitle")
            : t("professionals.invite.title")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            {inviteTarget && resolveProfessionalAccessStatus(inviteTarget) === "pending"
              ? t("professionals.invite.resendDescription", {
                  name: inviteTarget?.name ?? "",
                })
              : t("professionals.invite.description", {
                  name: inviteTarget?.name ?? "",
                })}
          </p>
          {inviteTarget &&
          resolveProfessionalAccessStatus(inviteTarget) === "pending" &&
          formatInviteSentAt(inviteTarget.invited_at, i18n.language) ? (
            <p className="text-muted small">
              {t("professionals.invite.sentAt", {
                date: formatInviteSentAt(inviteTarget.invited_at, i18n.language),
              })}
            </p>
          ) : null}
          {(() => {
            const inviteCooldownMs =
              inviteTarget &&
              resolveProfessionalAccessStatus(inviteTarget) === "pending"
                ? getInviteResendCooldownRemainingMs(inviteTarget.invited_at, nowTick)
                : 0;
            const inviteCooldownSeconds = formatInviteCooldownSeconds(inviteCooldownMs);

            return (
              <>
                {inviteCooldownMs > 0 ? (
                  <Alert color="info" className="mb-3">
                    {t("professionals.invite.resendCooldown", {
                      seconds: inviteCooldownSeconds,
                    })}
                  </Alert>
                ) : null}
                <Label htmlFor="professional-invite-email">
                  {t("professionals.invite.fields.email")}
                </Label>
                <Input
                  id="professional-invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder={t("professionals.invite.fields.emailPlaceholder")}
                  className="mb-3"
                />
                <div className="d-flex justify-content-end gap-2">
                  <Button color="light" onClick={closeInvite} disabled={inviteSubmitting}>
                    {t("professionals.actions.cancel")}
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => void submitInvite()}
                    disabled={inviteSubmitting || inviteCooldownMs > 0}
                  >
                    {inviteSubmitting ? <Spinner size="sm" className="me-1" /> : null}
                    {inviteTarget && resolveProfessionalAccessStatus(inviteTarget) === "pending"
                      ? t("professionals.invite.actions.resend")
                      : t("professionals.invite.actions.send")}
                  </Button>
                </div>
              </>
            );
          })()}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ProfessionalsList;
